import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { required, useTranslate } from "ra-core";
import { DateTimeInput } from "@/components/admin";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";

import { contactOptionText } from "../misc/ContactOption";
import { defaultTaskTypes } from "../root/defaultConfiguration";

const CONTACT_TASK_VALUES = [
  "send-dev-email", "send-catalog", "follow-inquiry", "confirm-requirements",
  "send-sample", "follow-sample-receipt", "follow-sample-approval",
  "confirm-receipt", "seasonal-greeting", "repurchase-followup",
];

const ORDER_TASK_VALUES = [
  "send-quotation", "follow-quotation", "send-pi", "follow-deposit",
  "follow-production", "arrange-inspection", "follow-balance",
  "follow-shipment", "handle-complaint", "negotiate-price",
];

const GroupedTaskTypeSelect = () => {
  const { setValue, watch } = useFormContext();
  const translate = useTranslate();
  const rawValue = watch("type");
  const selected = rawValue && rawValue !== "none" ? rawValue : null;

  // Always use hardcoded defaultTaskTypes — context cache may contain stale English values
  const labelMap = Object.fromEntries(defaultTaskTypes.map((t) => [t.value, t.label]));

  const renderCol = (title: string, values: string[]) => (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-muted-foreground border-b pb-1 mb-1.5">
        {title}
      </div>
      <div className="flex flex-col gap-1">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setValue("type", v, { shouldDirty: true, shouldValidate: true })}
            className={cn(
              "text-left text-sm px-2.5 py-1.5 rounded-md transition-colors w-full",
              selected === v
                ? "bg-primary text-primary-foreground font-medium"
                : "hover:bg-muted text-foreground",
            )}
          >
            {labelMap[v] ?? v}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium">
        {translate("resources.tasks.fields.type")}
        {selected && (
          <span className="ml-2 text-xs font-normal text-primary">
            ✓ {labelMap[selected]}
          </span>
        )}
      </label>
      <div className="border rounded-md p-2 flex flex-col sm:flex-row gap-3">
        {renderCol("联系人待办（10 类）", CONTACT_TASK_VALUES)}
        <div className="hidden sm:block w-px bg-border" />
        <div className="block sm:hidden h-px bg-border" />
        {renderCol("订单待办（10 类）", ORDER_TASK_VALUES)}
      </div>
    </div>
  );
};

export const TaskFormContent = ({
  selectContact,
}: {
  selectContact?: boolean;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        rows={3}
        className="m-0"
        helperText={false}
        placeholder="输入任务描述，例如：跟进客户报价反馈"
      />
      {selectContact && (
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput
            label="resources.tasks.fields.contact_id"
            optionText={contactOptionText}
            helperText={false}
            validate={required()}
            modal
          />
        </ReferenceInput>
      )}

      <DateTimeInput
        source="due_date"
        helperText={false}
        validate={required()}
      />
      <GroupedTaskTypeSelect />
    </div>
  );
};
